import { useMutation } from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import { CREATE_UPLOAD_INTENT_MUTATION } from '@/api/mutations/file/useCreateUploadIntent';
import { graphql } from '@/gql';
import { UploadFileInput } from '@/gql/graphql';

const PROCESS_UPLOAD_INTENT = graphql(`
  mutation ProcessUploadIntent($input: ProcessUploadIntentInput!) {
    processUploadIntent(input: $input) {
      id
      fileName
      url
      thumbnailUrl
      mimeType
    }
  }
`);

const useCreateUpload = () => {
  return useMutation({
    mutationFn: async (variables: {
      file: File;
      linkedEntities?: UploadFileInput['linkedEntities'];
    }) => {
      // Create upload intent
      const response = await gqlClient.request(CREATE_UPLOAD_INTENT_MUTATION, {
        input: {
          files: [
            {
              fileName: variables.file.name,
              mimeType: variables.file.type,
              linkedEntities: variables.linkedEntities || [],
            },
          ],
          skipAutoProcess: true,
        },
      });

      const { signedUploadUrl, signedUploadUrlHeaders, uploadIntent } =
        response.createUploadIntent[0];

      // Upload file to the signed URL
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', signedUploadUrl, true);
        signedUploadUrlHeaders?.forEach(([name, value]) =>
          xhr.setRequestHeader(name, value)
        );

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));

        xhr.send(variables.file);
      });

      // Trigger the server to process the upload
      return gqlClient.request(PROCESS_UPLOAD_INTENT, {
        input: {
          uploadIntent: {
            publicId: uploadIntent.publicId,
            synchronouslyGenerateThumbnail: true,
          },
        },
      });
    },
  });
};

export default useCreateUpload;
